
function getRoleFromPath() {
  if (typeof window === "undefined") return null;
  const path = window.location.pathname;
  if (path.startsWith("/director")) return "director";
  if (path.startsWith("/hr")) return "hr";
  if (path.startsWith("/super-admin")) return "super_admin";
  if (path.startsWith("/employee")) return "employee";
  return null;
}

function safeGet(storage, key) {
  try {
    const v = storage?.getItem(key);
    return v === "undefined" || v === "null" ? null : v;
  } catch {
    return null;
  }
}

function safeSet(storage, key, value) {
  try {
    storage?.setItem(key, value);
  } catch {
    // ignore (quota/private mode)
  }
}

function safeRemove(storage, key) {
  try {
    storage?.removeItem(key);
  } catch {
    // ignore
  }
}

function getPersistentStorage() {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function getSessionStorage() {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function clearLegacySessionStorageAuth(name, role = null) {
  // We now persist auth in localStorage. This function only cleans up
  // older sessionStorage keys to avoid drift between storages.
  if (typeof window === "undefined") return;
  try {
    if (role) window.sessionStorage.removeItem(`${role}_${name}`);
    window.sessionStorage.removeItem(name);
    if (name === "role") window.sessionStorage.removeItem("role");
  } catch {
    // ignore
  }
}

export function setCookie(name, value, days = 365, role = null) {
  if (typeof window !== "undefined") {
    const persistent = getPersistentStorage();
    const session = getSessionStorage();
    if (!persistent && !session) return;

    // 1. Determine active role (provided or detected)
    const activeRole = role || getRoleFromPath();
    
    // 2. Persistent storage for the role-specific token (always localStorage)
    if (activeRole) {
      const key = `${activeRole}_${name}`;
      safeSet(persistent, key, value);
      // Mirror into sessionStorage for compatibility with any older reads.
      safeSet(session, key, value);
      clearLegacySessionStorageAuth(name, activeRole);
    }
    
    // 3. Set a generic persistent fallback
    safeSet(persistent, name, value);
    safeSet(session, name, value);
    clearLegacySessionStorageAuth(name);
    
    // 4. Record role persistently
    if (name === "role" || role) {
      safeSet(persistent, "role", role || value);
      safeSet(session, "role", role || value);
      clearLegacySessionStorageAuth("role");
    }
  }
}

export function getCookie(name) {
  if (typeof window !== "undefined") {
    const persistent = getPersistentStorage();
    const session = getSessionStorage();
    if (!persistent && !session) return null;

    // 1. Try role from path
    let activeRole = getRoleFromPath();
    
    // 2. Fallback to persisted role (check both storages)
    if (!activeRole) {
      activeRole = safeGet(persistent, "role") || safeGet(session, "role");
    }

    // 3. Role-prefixed value
    if (activeRole && activeRole !== "null" && activeRole !== "undefined") {
      const key = `${activeRole}_${name}`;
      const val = safeGet(persistent, key) || safeGet(session, key);
      if (val) return val;
    }

    // 4. Generic persisted value
    const globalVal = safeGet(persistent, name) || safeGet(session, name);
    if (globalVal) return globalVal;
  }
  return null;
}

export function deleteCookie(name) {
  if (typeof window !== "undefined") {
    const persistent = getPersistentStorage();
    const session = getSessionStorage();
    const activeRole = getRoleFromPath();
    if (activeRole) {
      const key = `${activeRole}_${name}`;
      safeRemove(persistent, key);
      safeRemove(session, key);
    }
    safeRemove(persistent, name);
    safeRemove(session, name);

    // If you're clearing auth, also clear role to avoid role-prefixed drift.
    safeRemove(persistent, "role");
    safeRemove(session, "role");

    // Cleanup both storages (mode may differ between runs).
    try {
      window.localStorage.removeItem(name);
      window.localStorage.removeItem("role");
      window.sessionStorage.removeItem(name);
      window.sessionStorage.removeItem("role");
    } catch {
      // ignore
    }
  }
}

// Kept for compatibility
export function syncAllSessionToCookies() {
}

